import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";
import { InternBrandStyleComponentMapping_brand$key } from "./__generated__/InternBrandStyleComponentMapping_brand.graphql";
import useBrandAppConfigUpdate2Mutation from "../../../../mutations/useBrandAppConfigUpdate2Mutation";

const brandFragment = graphql`
  fragment InternBrandStyleComponentMapping_brand on BrandType {
    id
    internStyle {
      componentMappings {
        selector
        style
      }
    }
    appConfig {
      id
      htmlCustomAttributes {
        querySelector
        attributeKey
        attributeValue
      }
    }
  }
`;

type InternBrandStyleComponentMappingProps = {
  brand: InternBrandStyleComponentMapping_brand$key;
};

const mappingChipSx = {
  maxWidth: "100%",
  height: "auto",
  "& .MuiChip-label": {
    whiteSpace: "normal",
    display: "block",
    py: 0.5,
  },
};

const InternBrandStyleComponentMapping = ({
  brand: brandKey,
}: InternBrandStyleComponentMappingProps) => {
  const brand = useFragment(brandFragment, brandKey);
  const [updateBrandAppConfig, isMutationInFlight] =
    useBrandAppConfigUpdate2Mutation();

  // Get component mappings from intern style
  const internComponentMappings = useMemo(
    () => brand.internStyle?.componentMappings || [],
    [brand.internStyle?.componentMappings]
  );

  // Get existing HTML custom attributes
  const existingHTMLCustomAttributes = useMemo(
    () => brand.appConfig?.htmlCustomAttributes || [],
    [brand.appConfig?.htmlCustomAttributes]
  );

  // Filter out component mappings that already exist in htmlCustomAttributes
  const newComponentMappings = useMemo(() => {
    return internComponentMappings.filter((internMapping) => {
      return !existingHTMLCustomAttributes.some(
        (existingAttr) => existingAttr.querySelector === internMapping.selector
      );
    });
  }, [internComponentMappings, existingHTMLCustomAttributes]);

  // Check if there are changes to save
  const hasChanges = newComponentMappings.length > 0;

  const handleSave = () => {
    if (!brand.appConfig?.id) return;

    // Convert component mappings to HTML custom attributes format
    const newHTMLCustomAttributes = newComponentMappings.map((mapping) => ({
      querySelector: mapping.selector,
      attributeKey: "style",
      attributeValue: mapping.style,
    }));

    // Combine existing and new attributes
    const allHTMLCustomAttributes = [
      ...existingHTMLCustomAttributes.map((attr) => ({
        querySelector: attr.querySelector,
        attributeKey: attr.attributeKey,
        attributeValue: attr.attributeValue,
      })),
      ...newHTMLCustomAttributes,
    ];

    updateBrandAppConfig({
      id: brand.appConfig.id,
      htmlCustomAttributes: allHTMLCustomAttributes,
    });
  };

  return (
    <Card
      sx={{
        p: 2,
        width: "100%",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h6" mb={2} fontWeight={600}>
        Component Mappings
      </Typography>

      <Stack spacing={2} sx={{ minWidth: 0, flex: 1 }}>
        {/* Existing HTML Custom Attributes */}
        {existingHTMLCustomAttributes.length > 0 && (
          <Box>
            <Typography variant="subtitle2" mb={1} color="text.secondary">
              Existing Custom Attributes ({existingHTMLCustomAttributes.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, minWidth: 0 }}>
              {existingHTMLCustomAttributes.map((attr, index) => (
                <Chip
                  key={index}
                  label={`${attr.querySelector} → ${attr.attributeKey}: ${attr.attributeValue}`}
                  variant="outlined"
                  size="small"
                  color="success"
                  sx={mappingChipSx}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* New Component Mappings from Intern Style */}
        {newComponentMappings.length > 0 ? (
          <Box>
            <Typography variant="subtitle2" mb={1} color="text.secondary">
              New Component Mappings from Intern Style (
              {newComponentMappings.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, minWidth: 0 }}>
              {newComponentMappings.map((mapping, index) => (
                <Chip
                  key={index}
                  label={`${mapping.selector} → ${mapping.style}`}
                  variant="outlined"
                  size="small"
                  color="primary"
                  sx={mappingChipSx}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Alert severity="info">
            All component mappings from intern style are already saved as HTML
            custom attributes.
          </Alert>
        )}

        {/* Intern Style Component Mappings */}
        {internComponentMappings.length > 0 && (
          <Box>
            <Typography variant="subtitle2" mb={1} color="text.secondary">
              All Intern Style Component Mappings (
              {internComponentMappings.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, minWidth: 0 }}>
              {internComponentMappings.map((mapping, index) => {
                const isAlreadySaved = existingHTMLCustomAttributes.some(
                  (attr) => attr.querySelector === mapping.selector
                );
                return (
                  <Chip
                    key={index}
                    label={`${mapping.selector} → ${mapping.style}`}
                    variant={isAlreadySaved ? "filled" : "outlined"}
                    size="small"
                    color={isAlreadySaved ? "success" : "primary"}
                    sx={mappingChipSx}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </Stack>

      <CardActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 2,
          borderTop: "1px solid #DBDCDD",
          mt: "auto",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={!hasChanges || isMutationInFlight}
          onClick={handleSave}
        >
          {isMutationInFlight ? "Saving..." : "Save New Mappings"}
        </Button>
      </CardActions>
    </Card>
  );
};

export default InternBrandStyleComponentMapping;
